// Log a grievance a driver raised in person, on WhatsApp, or by an email that
// did not go to the grievances address (clause 31.2).
//
// IN PERSON, THE READ-BACK IS THE POINT. The manager writes what the driver
// said, reads it back to him, and ticks that he confirmed it. Postgres refuses
// the record without the tick, so a grievance cannot be put into his mouth.
//
// WHATSAPP, INTERIM. The contract promises WhatsApp text and voice notes. Until
// the real number is on the Cloud API (docs/WHATSAPP.md), whoever receives one
// logs it here the same day, with the message picked from the inbox or the
// voice note attached. Either way the recording is kept, in his own words.
//
// Nothing about routing is decided here. The page shows who it went to after
// Postgres has decided.
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { MessageSquarePlus, Paperclip } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { useLogGrievance } from '../../hooks/backend/grievances'
import { useGrievanceUpload } from '../../hooks/useGrievanceUpload'
import { formatDay, todaySast, type Options } from '../../utils/grievances'

type Form = {
  channel: string; employee_person_id: string; employee_name: string; employee_contact: string
  category: string; description: string; urgent: boolean; event_date: string; raised_on: string
  subject_person_id: string; subject_description: string; language: string; companion: string
  interpreter: string; read_back_confirmed: boolean; whatsapp_message_id: string
}

const empty = (): Form => ({
  channel: 'in_person', employee_person_id: '', employee_name: '', employee_contact: '', category: '',
  description: '', urgent: false, event_date: '', raised_on: todaySast(), subject_person_id: '',
  subject_description: '', language: '', companion: '', interpreter: '', read_back_confirmed: false,
  whatsapp_message_id: '',
})

type Logged = { ref: string; owner_name: string; stage: string; rule: string; reason: string; meet_by: string | null; answer_by: string; acknowledged_by_email: boolean; confidential: boolean; late: boolean }

function Field({ label, hint, children, htmlFor }: { label: string; hint?: ReactNode; children: ReactNode; htmlFor: string }) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="text-xs font-medium text-foreground">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function LogGrievanceDialog({ open, onOpenChange, options, onSaved }: {
  open: boolean; onOpenChange: (o: boolean) => void; options: Options | null; onSaved: () => void
}) {
  const log = useLogGrievance<Logged>()
  const { upload, uploading } = useGrievanceUpload()
  const [f, setF] = useState<Form>(empty)
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<Logged | null>(null)

  useEffect(() => { if (open) { setF(empty()); setFiles([]); setError(null); setDone(null) } }, [open])
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }))

  const isWhatsapp = f.channel === 'whatsapp_text' || f.channel === 'whatsapp_voice'
  const waMessages = useMemo(() => {
    const all = options?.whatsapp ?? []
    return f.employee_person_id ? [...all.filter((m) => m.person_id === f.employee_person_id), ...all.filter((m) => m.person_id !== f.employee_person_id)] : all
  }, [options, f.employee_person_id])

  const blockers: string[] = []
  if (!f.employee_person_id && !f.employee_name.trim()) blockers.push('who raised it')
  if (!f.category) blockers.push('what it is about')
  if (!f.description.trim()) blockers.push('his words')
  if (f.channel === 'in_person' && !f.read_back_confirmed) blockers.push('the read-back tick')
  if (f.channel === 'whatsapp_voice' && !f.whatsapp_message_id && files.length === 0) blockers.push('the voice note')

  async function save() {
    setError(null)
    try {
      const up = files.length ? await upload(files, null) : { done: [], failed: [] }
      if (up.failed.length) throw new Error(`Not saved, because a file did not upload. ${up.failed.join(' ')}`)
      const r = await log.trigger({
        ...f,
        employee_person_id: f.employee_person_id || null,
        subject_person_id: f.subject_person_id || null,
        whatsapp_message_id: f.whatsapp_message_id || null,
        event_date: f.event_date || null,
        attachments: up.done,
      })
      if (r) { setDone(r); onSaved() }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquarePlus className="h-5 w-5" /> Log a grievance</DialogTitle>
          <DialogDescription>
            A grievance a driver raised with you in person, on WhatsApp, or by email to you. Record it in writing the same day (clause 31.2). He is acknowledged in writing with a reference as soon as you save.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-3 text-sm">
            <p className="text-base font-semibold text-foreground">{done.ref} is on the register.</p>
            <p>It is with <b>{done.owner_name}</b> (clause {done.rule}). {done.reason}</p>
            <p>{done.meet_by ? `Meet him by ${formatDay(done.meet_by)}. ` : ''}Answer in writing by {formatDay(done.answer_by)}.</p>
            {!done.acknowledged_by_email && (
              <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                He has no email address on record, so the acknowledgement could not be emailed. Give him the reference {done.ref} in writing today, on WhatsApp or on paper, and record that on the grievance.
              </p>
            )}
            {done.late && <p className="text-amber-800 dark:text-amber-200">It was raised more than 30 days after the event. The holder records his explanation and whether it is accepted (31.3).</p>}
            <div className="flex justify-end"><Button size="sm" onClick={() => onOpenChange(false)}>Done</Button></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="How it reached you" htmlFor="g-channel">
                <NativeSelect id="g-channel" value={f.channel} onChange={(e) => set('channel', e.target.value)}>
                  <option value="in_person">In person</option>
                  <option value="whatsapp_text">WhatsApp text</option>
                  <option value="whatsapp_voice">WhatsApp voice note</option>
                  <option value="email">Email to me</option>
                </NativeSelect>
              </Field>
              <Field label="The day he raised it" htmlFor="g-raised" hint="Deadlines run from this day, even if you record it later.">
                <Input id="g-raised" type="date" max={todaySast()} value={f.raised_on} onChange={(e) => set('raised_on', e.target.value)} />
              </Field>
            </div>

            <Field label="Who raised it" htmlFor="g-emp">
              <NativeSelect id="g-emp" value={f.employee_person_id} onChange={(e) => set('employee_person_id', e.target.value)}>
                <option value="">Not on the list: type his name below</option>
                {(options?.employees ?? []).map((p) => (
                  <option key={p.person_id} value={p.person_id}>{p.name}{p.kind === 'staff' ? ' (staff)' : p.manager ? `, ${p.manager}` : ''}{p.has_email ? '' : ', no email'}</option>
                ))}
              </NativeSelect>
            </Field>
            {!f.employee_person_id && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="His name" htmlFor="g-name"><Input id="g-name" value={f.employee_name} onChange={(e) => set('employee_name', e.target.value)} /></Field>
                <Field label="His phone or email" htmlFor="g-contact"><Input id="g-contact" value={f.employee_contact} onChange={(e) => set('employee_contact', e.target.value)} /></Field>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="What it is about" htmlFor="g-cat">
                <NativeSelect id="g-cat" value={f.category} onChange={(e) => set('category', e.target.value)}>
                  <option value="">Choose</option>
                  {(options?.categories ?? []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </NativeSelect>
              </Field>
              <Field label="When it happened" htmlFor="g-event" hint="More than 30 days ago is still heard; he will be asked why it is late. Unpaid pay can be raised at any time.">
                <Input id="g-event" type="date" max={f.raised_on || todaySast()} value={f.event_date} onChange={(e) => set('event_date', e.target.value)} />
              </Field>
            </div>

            <Field label="Who it is about, if anyone" htmlFor="g-subject" hint="This decides who answers it. The person it is about never decides it and never sees it.">
              <NativeSelect id="g-subject" value={f.subject_person_id} onChange={(e) => set('subject_person_id', e.target.value)}>
                <option value="">Nobody in particular, or someone not on this list</option>
                {(options?.staff ?? []).map((s) => <option key={s.person_id} value={s.person_id}>{s.name} ({s.roles})</option>)}
                {(options?.employees ?? []).filter((p) => p.kind === 'driver').map((p) => <option key={`d-${p.person_id}`} value={p.person_id}>{p.name} (driver)</option>)}
              </NativeSelect>
            </Field>
            {!f.subject_person_id && (
              <Field label="Or describe who it is about" htmlFor="g-subject-d"><Input id="g-subject-d" value={f.subject_description} onChange={(e) => set('subject_description', e.target.value)} placeholder="For example, a passenger, or someone at a supplier" /></Field>
            )}

            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={f.urgent} onChange={(e) => set('urgent', e.target.checked)} />
              <span>Urgent: it is about his safety, violence or threats, or pay that is unpaid or late. It goes straight to the Operational Servant, answered within 2 working days (31.9).</span>
            </label>

            {isWhatsapp && (
              <Field label="The WhatsApp message" htmlFor="g-wa" hint={options?.whatsapp ? 'Pick it from the inbox, or attach the voice note or a screenshot below.' : 'You do not have the WhatsApp inbox. Attach the voice note or a screenshot below.'}>
                {options?.whatsapp ? (
                  <NativeSelect id="g-wa" value={f.whatsapp_message_id} onChange={(e) => set('whatsapp_message_id', e.target.value)}>
                    <option value="">Not in the inbox (it came to my own phone)</option>
                    {waMessages.map((m) => <option key={m.id} value={m.id}>{formatDay(m.at)}, {m.name ?? m.from}: {m.type === 'text' ? m.body.slice(0, 60) : m.type}</option>)}
                  </NativeSelect>
                ) : null}
              </Field>
            )}

            <Field label="What he said, in his words" htmlFor="g-desc" hint="Any language. Write it as he said it, not as a summary.">
              <Textarea id="g-desc" rows={6} value={f.description} onChange={(e) => set('description', e.target.value)} />
            </Field>

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Language he used" htmlFor="g-lang"><Input id="g-lang" value={f.language} onChange={(e) => set('language', e.target.value)} placeholder="isiZulu, Sesotho…" /></Field>
              <Field label="Fellow employee or union rep with him" htmlFor="g-comp"><Input id="g-comp" value={f.companion} onChange={(e) => set('companion', e.target.value)} /></Field>
              <Field label="Interpreter" htmlFor="g-int"><Input id="g-int" value={f.interpreter} onChange={(e) => set('interpreter', e.target.value)} /></Field>
            </div>

            <Field label="Photos, documents or the voice note" htmlFor="g-files">
              <label htmlFor="g-files" className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent/40">
                <Paperclip className="h-4 w-4" /> {files.length ? files.map((x) => x.name).join(', ') : 'Choose files'}
              </label>
              <input id="g-files" type="file" multiple className="sr-only" accept="image/*,audio/*,video/*,application/pdf,.doc,.docx"
                onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
            </Field>

            {f.channel === 'in_person' && (
              <label className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
                <input type="checkbox" className="mt-1" checked={f.read_back_confirmed} onChange={(e) => set('read_back_confirmed', e.target.checked)} />
                <span>I read this back to him and he confirmed it is what he said.</span>
              </label>
            )}

            {error && <p className="rounded-md bg-rose-50 p-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
            <div className="flex flex-wrap items-center justify-end gap-2">
              {blockers.length > 0 && <span className="text-xs text-muted-foreground">Still to fill: {blockers.join(', ')}.</span>}
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button variant="brand" size="sm" disabled={blockers.length > 0 || log.loading || uploading} onClick={() => void save()}>
                {(log.loading || uploading) && <Spinner className="mr-1 h-4 w-4" />} Save and acknowledge
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
